import { useEffect, useState, useCallback } from 'react';

interface Props {
  onClose: () => void;
}

interface Stats {
  messages: { last24h: number; last7d: number; last30d: number; twitch24h: number; youtube24h: number };
  users: { unique7d: number };
  events: { last7d: number };
  auditLog: { total: number };
}

interface Health {
  postgres: boolean;
  redis: boolean;
  wsClients: number;
  allOk: boolean;
}

interface GdprState {
  loading: boolean;
  result: string | null;
  error: string | null;
}

export default function AdminPage({ onClose }: Props) {
  const [stats, setStats] = useState<Stats | null>(null);
  const [health, setHealth] = useState<Health | null>(null);
  const [statsError, setStatsError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [gdpr, setGdpr] = useState<GdprState>({ loading: false, result: null, error: null });
  const [gdprUsername, setGdprUsername] = useState('');
  const [previewBusy, setPreviewBusy] = useState(false);
  const [previewMsg, setPreviewMsg] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    const [sRes, hRes] = await Promise.all([
      fetch('/api/admin/stats', { credentials: 'include' }),
      fetch('/api/admin/health', { credentials: 'include' }),
    ]);
    if (sRes.ok) {
      setStats((await sRes.json()) as Stats);
      setStatsError(null);
    } else {
      setStatsError(`Stats: HTTP ${sRes.status}`);
    }
    if (hRes.ok) setHealth((await hRes.json()) as Health);
    setLastUpdated(new Date());
  }, []);

  useEffect(() => {
    void loadData();
    const interval = setInterval(() => void loadData(), 30_000);
    return () => clearInterval(interval);
  }, [loadData]);

  const handleGdprDelete = async (e: React.FormEvent) => {
    e.preventDefault();
    const username = gdprUsername.trim();
    if (!username) return;
    if (!window.confirm(`Alle Nachrichten von „${username}" unwiderruflich anonymisieren?`)) return;
    setGdpr({ loading: true, result: null, error: null });
    try {
      const res = await fetch('/api/gdpr/delete', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username }),
      });
      const body = (await res.json()) as { deleted?: number; error?: string };
      if (res.ok) {
        setGdpr({ loading: false, result: `${body.deleted ?? 0} Nachrichten anonymisiert.`, error: null });
        setGdprUsername('');
      } else {
        const msg =
          body.error === 'not_found' ? 'Kein Eintrag gefunden.' :
          body.error === 'forbidden' ? 'Keine Berechtigung.' :
          `Fehler: ${body.error ?? res.status}`;
        setGdpr({ loading: false, result: null, error: msg });
      }
    } catch {
      setGdpr({ loading: false, result: null, error: 'Anfrage fehlgeschlagen.' });
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col">
      <header className="flex items-center gap-3 px-4 py-3 border-b border-slate-800 flex-shrink-0">
        <button
          type="button"
          onClick={onClose}
          className="text-slate-400 hover:text-slate-200 text-sm underline"
        >
          ← Zurück
        </button>
        <h1 className="text-base font-semibold">Admin-Panel</h1>
        <div className="ml-auto flex items-center gap-3">
          {lastUpdated && (
            <span className="text-xs text-slate-600">
              {lastUpdated.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </span>
          )}
          <button
            type="button"
            onClick={() => void loadData()}
            className="text-xs text-slate-500 hover:text-slate-300"
          >
            Aktualisieren
          </button>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-6">

        {/* Health */}
        <section>
          <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">System-Status</h2>
          {health ? (
            <div className="grid grid-cols-3 gap-2">
              <Ampel label="PostgreSQL" ok={health.postgres} />
              <Ampel label="Redis" ok={health.redis} />
              <div className="bg-slate-900 border border-slate-800 rounded px-3 py-2 text-center">
                <div className="text-lg font-bold text-purple-300">{health.wsClients}</div>
                <div className="text-xs text-slate-500 mt-0.5">WS-Clients</div>
              </div>
            </div>
          ) : (
            <p className="text-slate-500 text-sm">Lädt…</p>
          )}
        </section>

        {/* Stats */}
        <section>
          <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Nachrichten-Statistik</h2>
          {statsError && <p className="text-red-400 text-sm">{statsError}</p>}
          {stats ? (
            <>
              <div className="grid grid-cols-3 gap-2 mb-2">
                <StatCard label="Letzte 24h" value={stats.messages.last24h} />
                <StatCard label="Letzte 7 Tage" value={stats.messages.last7d} />
                <StatCard label="Letzte 30 Tage" value={stats.messages.last30d} />
              </div>
              <div className="grid grid-cols-2 gap-2 mb-2">
                <StatCard label="Twitch (24h)" value={stats.messages.twitch24h} sub="twitch" />
                <StatCard label="YouTube (24h)" value={stats.messages.youtube24h} sub="youtube" />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <StatCard label="Unique User (7d)" value={stats.users.unique7d} />
                <StatCard label="Events (7d)" value={stats.events.last7d} />
                <StatCard label="Audit-Log gesamt" value={stats.auditLog.total} />
              </div>
            </>
          ) : !statsError ? (
            <p className="text-slate-500 text-sm">Lädt…</p>
          ) : null}
        </section>

        {/* Vorschau-Modus */}
        <section>
          <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Test / Vorschau</h2>
          <p className="text-xs text-slate-500 mb-3">
            Setzt den Stream-Status auf „live" ohne einen echten Twitch-Stream — alle verbundenen Clients empfangen sofort das stream_online-Event.
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={previewBusy}
              onClick={() => {
                setPreviewBusy(true);
                setPreviewMsg(null);
                fetch('/api/admin/preview-live', { method: 'POST', credentials: 'include' })
                  .then((r) => r.json() as Promise<{ ok?: boolean; startedAt?: string }>)
                  .then((body) => setPreviewMsg(body.ok ? `▶ Aktiv seit ${new Date(body.startedAt!).toLocaleTimeString('de-DE')}` : 'Fehler'))
                  .catch(() => setPreviewMsg('Anfrage fehlgeschlagen.'))
                  .finally(() => setPreviewBusy(false));
              }}
              className="bg-purple-700 hover:bg-purple-600 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium px-4 py-1.5 rounded transition-colors"
            >
              {previewBusy ? '…' : '▶ Als Live markieren'}
            </button>
            <button
              type="button"
              disabled={previewBusy}
              onClick={() => {
                setPreviewBusy(true);
                setPreviewMsg(null);
                fetch('/api/admin/preview-live', { method: 'DELETE', credentials: 'include' })
                  .then(() => setPreviewMsg('⏹ Vorschau beendet'))
                  .catch(() => setPreviewMsg('Anfrage fehlgeschlagen.'))
                  .finally(() => setPreviewBusy(false));
              }}
              className="bg-slate-700 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium px-4 py-1.5 rounded transition-colors"
            >
              {previewBusy ? '…' : '⏹ Vorschau beenden'}
            </button>
          </div>
          {previewMsg && <p className="text-green-400 text-xs mt-2">{previewMsg}</p>}
        </section>

        {/* DSGVO */}
        <section>
          <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">DSGVO — Nachrichten anonymisieren</h2>
          <p className="text-xs text-slate-500 mb-3">
            Alle Nachrichten eines Nutzers werden anonymisiert (username + Inhalt → [gelöscht]).
            Dieser Vorgang kann nicht rückgängig gemacht werden.
          </p>
          <form onSubmit={(e) => void handleGdprDelete(e)} className="flex gap-2">
            <input
              type="text"
              value={gdprUsername}
              onChange={(e) => setGdprUsername(e.target.value)}
              placeholder="Twitch-Username"
              className="flex-1 bg-slate-800 border border-slate-700 rounded px-3 py-1.5 text-sm focus:outline-none focus:border-red-500 placeholder:text-slate-500"
              maxLength={100}
              autoComplete="off"
            />
            <button
              type="submit"
              disabled={gdpr.loading || !gdprUsername.trim()}
              className="bg-red-700 hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium px-4 py-1.5 rounded transition-colors"
            >
              {gdpr.loading ? 'Läuft…' : 'Anonymisieren'}
            </button>
          </form>
          {gdpr.result && (
            <p className="text-green-400 text-xs mt-2">{gdpr.result}</p>
          )}
          {gdpr.error && (
            <p className="text-red-400 text-xs mt-2">{gdpr.error}</p>
          )}
        </section>

      </div>
    </div>
  );
}

function Ampel({ label, ok }: { label: string; ok: boolean }) {
  return (
    <div className="bg-slate-900 border border-slate-800 rounded px-3 py-2 flex items-center gap-2">
      <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${ok ? 'bg-green-500' : 'bg-red-500'}`} />
      <span className="text-xs text-slate-300">{label}</span>
    </div>
  );
}

function StatCard({ label, value, sub }: { label: string; value: number; sub?: string }) {
  const color =
    sub === 'twitch' ? 'text-purple-300' :
    sub === 'youtube' ? 'text-red-400' :
    'text-slate-100';
  return (
    <div className="bg-slate-900 border border-slate-800 rounded px-3 py-2 text-center">
      <div className={`text-lg font-bold ${color}`}>{value.toLocaleString('de-DE')}</div>
      <div className="text-xs text-slate-500 mt-0.5">{label}</div>
    </div>
  );
}
